import type { NodeDataType } from './GraphType';
import type { SpeechType } from './NormalEdge';
import type { Node, NodeProps } from '@xyflow/react';
import React, { useState, useLayoutEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { IconButton, Tooltip, useTheme } from '@mui/material';
import { Handle, Position, useReactFlow } from '@xyflow/react';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { useStore } from '@stores/RootStore';
import { fonts } from '@theme/fontsCustomer';
import { ConditionIcon } from '@icons/ConditionIcon';
import { DangerFillIcon } from '@icons/DangerFillIcon';
import { PlusIcon } from '@icons/PlusIcon';
import { TrashIcon } from '@icons/TrashIcon';
import { WorkflowActionDTOModel } from '@repositories/workflowRepository/models/WorkflowActionDTOModel';
import { SelectNodeSpeech } from './SelectNodeSpeech';

// Node type with custom data for v12 compatibility
type ConditionNodeType = Node<NodeDataType, 'condition'>;
type Props = NodeProps<ConditionNodeType>;

export const ConditionNode = observer((node: Props) => {
  const { data, isConnectable: isNodeConnectable } = node;
  const { t } = useTranslation();
  const theme = useTheme();
  const { workflowDetailStore } = useStore();
  const { fitView } = useReactFlow();
  const isEditable = workflowDetailStore.checkEditable();
  const isSelected = workflowDetailStore.curNodeId === node.id;
  const [showMenu, setShowMenu] = useState<boolean>(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const [buttonRef, setButtonRef] = useState<HTMLElement | null>(null);

  /**
   * 노드 추가 후 뷰 조정 (전체 보기로 이동)
   * 이중 requestAnimationFrame으로 DOM 업데이트 완료 후 실행
   */
  const adjustViewAfterNodeAdd = useCallback(() => {
    // 이중 rAF: 브라우저가 레이아웃 계산을 완료한 후 fitView 실행
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        fitView({ padding: 0.2, duration: 500 });
      });
    });
  }, [fitView]);
  // 현재 노드에 연결된 조건 엣지 확인
  const conditionThenEdge = workflowDetailStore.findEdgeBySource(node.id, 'conditionThen');
  // 노드 연결 가능 여부
  const isConnectable = isNodeConnectable && isEditable;

  /**
   * 조건 선택 클릭
   * @param e 이벤트
   */
  const handleClickConditionBehavior = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    workflowDetailStore.curNodeId = node.id;
    workflowDetailStore.setToolbarView(isEditable ? 'conditionSelect' : 'conditionConfig');
  };

  /**
   * 조건 실행 결과 클릭
   * @param e 이벤트
   */
  const handleClickConditionExecResult = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    workflowDetailStore.curNodeId = node.id;
    workflowDetailStore.setToolbarView('conditionExec');
  };

  /**
   * 조건 삭제 클릭
   * @param e 이벤트
   * @param node 노드
   */
  const handleClickTrash = (e: React.MouseEvent<HTMLButtonElement>, node: NodeProps) => {
    e.stopPropagation();
    workflowDetailStore.removeActionNode(node.id);
    workflowDetailStore.setToolbarView('');
  };

  /**
   * 노드 클릭
   */
  const handleClickNode = () => {
    workflowDetailStore.curNodeId = node.id;
    workflowDetailStore.setToolbarView(isEditable ? 'conditionSelect' : 'conditionConfig');
  };

  /**
   * 조건 설정이 완료되지 않았는지 체크
   */
  const isConditionConfigInvalid = () => {
    const actionModel = data.actionModel;

    // 조건이 선택되지 않은 경우
    if (!actionModel || actionModel.type !== 'IF_ACTION') {
      return true;
    }

    // actionArguments 검사
    const actionArguments = actionModel.actionArguments;
    if (!actionArguments || actionArguments.length === 0) {
      return true;
    }

    // 필수 파라미터가 모두 채워져있는지 확인
    return actionArguments.some(arg => {
      if (!arg.expression || arg.expression.trim() === '') {
        return true;
      }
      return false;
    });
  };

  const showDangerIcon = isConditionConfigInvalid();

  /**
   * 조건 Then 액션 추가 클릭
   * @param e 이벤트
   */
  const handleClickConditionThenBtn = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setShowMenu(prev => !prev);
  };

  /**
   * 메뉴 위치 계산
   */
  useLayoutEffect(() => {
    if (showMenu && buttonRef) {
      const buttonRect = buttonRef.getBoundingClientRect();
      // 버튼의 중앙 아래에 메뉴 표시
      const x = buttonRect.left + buttonRect.width / 2 - 136; // 메뉴 너비(272px)의 절반
      const y = buttonRect.bottom + 16; // 버튼 아래 + 약간의 간격
      setMenuPosition({ x, y });
    }
  }, [showMenu, buttonRef]);

  /**
   * 메뉴에서 액션/조건 선택
   */
  const handleClickSpeech = (e: React.MouseEvent, type: SpeechType) => {
    e.stopPropagation();
    if (type === 'ACTION') {
      workflowDetailStore.addConditionThenActionNode(node.id);
      const newEdge = workflowDetailStore.findEdgeBySource(node.id, 'conditionThen');
      if (newEdge) {
        workflowDetailStore.curNodeId = newEdge.target;
        workflowDetailStore.setToolbarView('actionSelect');
      }
      adjustViewAfterNodeAdd();
    } else if (type === 'CONDITION') {
      // 조건 분기 액션 모델 생성
      const conditionActionModel = new WorkflowActionDTOModel({
        type: 'IF_ACTION',
        actionName: t('Workflow_Create_IfAction'),
        actionMethod: '',
        actionArguments: [
          {
            argumentName: 'expr',
            expression: '',
            expressionType: 'SIMPLE',
          },
        ],
      });

      // 조건 Then 노드를 조건으로 추가
      const newConditionNode = workflowDetailStore.addConditionThenConditionNode(node.id, conditionActionModel);

      runInAction(() => {
        workflowDetailStore.curNodeId = newConditionNode.id;
        workflowDetailStore.setToolbarView('conditionSelect');
      });
      adjustViewAfterNodeAdd();
    }

    setShowMenu(false);
  };

  // 조건 이름 - 사용자가 설정한 조건 표시
  const conditionName = data.actionModel?.actionArguments?.[0]?.expression?.trim() || t('Workflow_Create_IfAction');

  return (
    <Container isSelected={isSelected} onClick={handleClickNode}>
      <Type>
        <span>
          <ConditionIcon width={16} height={16} />
          <span>{`${t('Workflow_Create_78')} ${data.conditionIndex ?? 0}`}</span>
        </span>
        <TypeRight>
          {showDangerIcon && (
            <Tooltip title={t('Workflow_Validation_RequiredFieldsNotSet')} placement="bottom" arrow>
              <DangerIconWrapper>
                <DangerFillIcon width={16} height={16} />
              </DangerIconWrapper>
            </Tooltip>
          )}
          {isEditable && (
            <IconBtn onClick={e => handleClickTrash(e, node)}>
              <TrashIcon width={14} height={14} />
            </IconBtn>
          )}
        </TypeRight>
      </Type>
      <>
        <Behavior
          isSelected={isSelected && workflowDetailStore.toolbar === 'conditionSelect'}
          hasConditionName
          onClick={handleClickConditionBehavior}
        >
          <BehaviorTitleBox>
            <ConditionText>{conditionName}</ConditionText>
          </BehaviorTitleBox>
        </Behavior>
        {workflowDetailStore.execution && (
          <Config
            onClick={handleClickConditionExecResult}
            isSelected={isSelected && workflowDetailStore.toolbar === 'conditionExec'}
          >
            {t('Workflow.graph.execResult')}
          </Config>
        )}
      </>
      {isEditable && isSelected && !conditionThenEdge && (
        <SideButton ref={setButtonRef}>
          <Add onClick={handleClickConditionThenBtn} isActive={showMenu}>
            <PlusIcon color={showMenu ? theme.colors['ic-primary'] : theme.colors['ic-gray-main']} />
          </Add>
        </SideButton>
      )}
      {/* 액션/조건 선택 메뉴 - Portal로 렌더링 */}
      {isEditable && showMenu && <SelectNodeSpeech menuPosition={menuPosition} onClickSpeech={handleClickSpeech} />}
      <Handle
        type="source"
        position={Position.Bottom}
        id="normal"
        isConnectable={isConnectable}
        style={{
          width: 8,
          height: 8,
          background: '#fff',
          border: '2px solid #9CA3AF',
        }}
      />
      <Handle
        type="target"
        position={Position.Top}
        id="normal"
        isConnectable={isConnectable}
        style={{
          width: 8,
          height: 8,
          background: '#fff',
          border: '2px solid #9CA3AF',
        }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="conditionThen"
        isConnectable={isConnectable}
        style={{
          width: 8,
          height: 8,
          background: '#fff',
          border: '2px solid #9CA3AF',
        }}
      />
    </Container>
  );
});

export const MemoizedConditionNode = React.memo(ConditionNode);

const Container = styled.div<{ isSelected: boolean }>`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;

  border: ${props =>
    props.isSelected
      ? `2px solid ${props.theme.colors['border-primary']}`
      : `1px solid ${props.theme.colors['border-gray-lighter']}`};
  outline: ${props => (props.isSelected ? `4px solid ${props.theme.colors['border-primary-light']}` : 'none')};
  height: fit-content;
  width: '392px';
  background: ${props => props.theme.colors['bg-gray-light']};
  border-radius: 4px;
  padding: ${props => (props.isSelected ? '7px' : '8px')};
  box-shadow: ${props =>
    props.isSelected
      ? '0px 8px 10px -5px rgba(0, 0, 0, 0.08), 0px 16px 24px 2px rgba(0, 0, 0, 0.04), 0px 6px 30px 5px rgba(0, 0, 0, 0.05)'
      : 'none'};
`;

const Type = styled.div`
  display: flex;
  flex-direction: row;
  margin: 0px 4px;

  ${fonts.Headline9};
  align-items: center;
  margin-bottom: 4px;
  justify-content: space-between;
  & > span {
    display: flex;
    flex-direction: row;
    gap: 4px;
    align-items: center;
  }
`;

const TypeRight = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: center;
`;

const DangerIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const Behavior = styled.div<{ isSelected: boolean; hasConditionName: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 40px;
  border-radius: 3px;
  border-bottom-left-radius: ${props => (props.isSelected ? '0px' : '3px')};
  border-bottom-right-radius: ${props => (props.isSelected ? '0px' : '3px')};
  padding: 0px 16px;
  border: 1px solid ${props => props.theme.colors['border-gray-lighter']};
  background: ${props => props.theme.colors['bg-white']};

  ${props => (props.isSelected ? fonts.Headline8 : fonts.Body2)};
  color: ${props =>
    props.isSelected
      ? props.theme.colors['text-primary']
      : props.hasConditionName
      ? props.theme.colors['text-gray-main']
      : props.theme.colors['text-gray-light']};
`;

const Config = styled.div<{ isSelected: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 40px;
  border-radius: 3px;
  border-top-left-radius: 0px;
  border-top-right-radius: 0px;
  padding: 0px 16px;
  border: 1px solid ${props => props.theme.colors['border-gray-lighter']};
  border-top: 0px;
  background: ${props => props.theme.colors['bg-white']};
  ${props => (props.isSelected ? fonts.Headline8 : fonts.Body2)};
  color: ${props => (props.isSelected ? props.theme.colors['text-primary'] : props.theme.colors['text-gray-main'])};
`;

const IconBtn = styled(IconButton)`
  width: 24px;
  height: 24px;
  background: ${props => props.theme.colors['bg-white']};
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  border-radius: 2px;
  border: 1px solid ${props => props.theme.colors['border-gray-lighter']};
`;

const SideButton = styled(IconButton)<{ borderColor?: string }>`
  position: absolute;
  top: 50%;
  right: -30px; /* 노드의 오른쪽 밖으로 약간 이동 */
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  min-width: 24px;
  padding: 0;
  border-radius: 50%;
  background: ${props => props.theme.colors['bg-white']};
  border: 1px solid ${props => props.borderColor ?? props.theme.colors['border-gray-light']};
`;

const Add = styled.div<{ isActive?: boolean }>`
  display: flex;
  flex-direction: row;
  width: 24px;
  height: 24px;
  align-items: center;
  border: ${props => (props.isActive ? '2px' : '1px')} solid
    ${props => (props.isActive ? props.theme.colors['border-primary'] : props.theme.colors['border-gray-dark'])};
  border-radius: 3px;
  background: ${props => (props.isActive ? props.theme.colors['bg-primary-lighter'] : props.theme.colors['bg-white'])};
  justify-content: center;
  :hover {
    border: 1px solid ${props => props.theme.colors['border-primary']};
    background-color: ${props => props.theme.colors['bg-primary-light-50']};
  }
`;

const BehaviorTitleBox = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  width: 100%;
  overflow: hidden;
`;

const ConditionText = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;
